export interface FlatRow {
  id: string;
  parentId: string | null;
  depth: number;
  position: number;
}

export interface DropTarget {
  /** null = promote to top-level; otherwise nest under this top-level row id. */
  parentRowId: string | null;
  position: number;
}

/** One top-level entry for {@link flattenGroupRows}: the row plus its (already
 * position-sorted) children and whether it is currently expanded. */
export interface GroupRowEntry {
  id: string;
  position: number;
  expanded: boolean;
  children: { id: string; position: number }[];
}

/**
 * Binary projected depth for a sub-item drag, derived from the drag's horizontal
 * delta. Sub-items start nested at depth 1; pulling left past ~half an indent
 * step promotes to depth 0, otherwise the drop stays nested. The v1 tree caps at
 * depth 1, so the result is only ever 0 or 1.
 */
export function projectDepth(deltaX: number, indent = 24): 0 | 1 {
  return Math.round(1 + deltaX / indent) <= 0 ? 0 : 1;
}

/**
 * Build the visible flattened `FlatRow[]` for one group — each top-level row
 * immediately followed by its children when expanded — matching the order the
 * SortableContext renders. This is the shape {@link resolveSubitemDrop} expects.
 */
export function flattenGroupRows(entries: GroupRowEntry[]): FlatRow[] {
  return entries.flatMap((r) => {
    const self: FlatRow = { id: r.id, parentId: null, depth: 0, position: r.position };
    const kids: FlatRow[] = r.expanded
      ? r.children.map((c) => ({ id: c.id, parentId: r.id, depth: 1, position: c.position }))
      : [];
    return [self, ...kids];
  });
}

// The board orders rows by a decimal `position`; new slots take the midpoint of
// their neighbours. A null neighbour means "no row on that side" → step ±1000,
// matching the rest of the app's ordering scheme.
const POSITION_STEP = 1000;

function midpoint(prev: number | null, next: number | null): number {
  if (prev === null) {
    return next === null ? 0 : next - POSITION_STEP;
  }
  if (next === null) {
    return prev + POSITION_STEP;
  }
  return (prev + next) / 2;
}

/**
 * Resolve where a dragged sub-item should land.
 *
 * The visible order is a flattened tree: each top-level row immediately followed
 * by its expanded children. The intended parent is decided by `projectedDepth`:
 *   - depth 0 → promote to top-level (parentRowId = null).
 *   - depth 1 → nest under the over row's top-level ancestor. Dropping onto a
 *     top-level row nests under it; dropping onto a child nests under that
 *     child's parent. We never nest under a sub-item (depth-1 cap).
 *
 * Position is the midpoint between the drop slot's same-parent neighbours after
 * the dragged row is removed and re-inserted at the slot implied by `overId`.
 *
 * Returns `null` for a no-op: unchanged parent AND unchanged order.
 */
export function resolveSubitemDrop(args: {
  rows: FlatRow[];
  activeId: string;
  overId: string;
  projectedDepth: 0 | 1;
}): DropTarget | null {
  const { rows, activeId, overId, projectedDepth } = args;

  const from = rows.findIndex((r) => r.id === activeId);
  const to = rows.findIndex((r) => r.id === overId);
  if (from < 0 || to < 0) return null;

  const active = rows[from];
  const over = rows[to];
  if (!active || !over) return null;

  // Resolve the intended parent. At depth 1 the over row decides it: a top-level
  // over row is the parent itself; a child over row hands us its own parent so we
  // never nest under a sub-item.
  const parentRowId: string | null =
    projectedDepth === 0 ? null : over.depth === 0 ? over.id : over.parentId;

  // Rebuild the visible order with the active row moved into the over slot.
  const without = rows.filter((r) => r.id !== activeId);
  const overSlot = without.findIndex((r) => r.id === overId);
  const insertAt = overSlot + (to > from ? 1 : 0);
  const ordered = [...without.slice(0, insertAt), active, ...without.slice(insertAt)];
  const idx = ordered.findIndex((r) => r.id === activeId);

  const isSibling = (r: FlatRow): boolean =>
    r.id !== activeId && (parentRowId === null ? r.depth === 0 : r.parentId === parentRowId);

  const before = ordered.slice(0, idx).reverse().find(isSibling) ?? null;
  const after = ordered.slice(idx + 1).find(isSibling) ?? null;

  const position = midpoint(before ? before.position : null, after ? after.position : null);

  // No-op: parent unchanged AND position in the order unchanged.
  if (parentRowId === active.parentId && from === to) return null;

  return { parentRowId, position };
}
